import React, { useCallback, useEffect, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { SketchPicker } from "react-color";
import type { ColorResult } from "react-color";
import { getAdCampaigns, getAdById, getPlacements, getTournamentsForAds } from "../../../../api/ads";
import type { AdButton, AdButtonRedirectionType, CreateAdPayload, UpdateAdPayload } from "../../../../api/ads/types";
import { getAcademyDropdown } from "../../../../api/academy/academyService";
import { getTournamentDropdown } from "../../../../api/tournament/tournamentService";
import { getWeeklyReportDropdown } from "../../../../api/weeklyReport/weeklyReportService";
import { getAllTournamentSeasons } from "../../../../api/tournament/manageTournamentService";
import type { TournamentSeason } from "../../../../api/tournament/manageTournamentService";

import "./CreateAdModal.css";

// ─── local types ────────────────────────────────────────────────────────────

interface SelectOption {
  value: string;
  label: string;
}

interface PlacementOption extends SelectOption {
  type: string;
}

interface FormState {
  campaign_id: string;
  campaign_name: string;
  placement_id: string;
  placement_type: string;
  placement_label: string;
  creative_type: "IMAGE" | "VIDEO" | "GIF" | "";
  media_url: string;
  thumbnail_url: string;
  click_url: string;
  is_internal: boolean;
  aspect_ratio: string;
  duration: number | "";
  platform_name: string;
  tournament_ids: string[];
  geo: string[];
  platform: string;
  banner_position: number | "";
  buttons: AdButton[];
  status: string;
}

// ─── constants ───────────────────────────────────────────────────────────────

const CREATIVE_TYPE_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "IMAGE", label: "Image" },
  { value: "VIDEO", label: "Video" },
  { value: "GIF", label: "GIF" },
];

const PLATFORM_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "app", label: "App" },
  { value: "web", label: "Web" },
  { value: "all", label: "All" },
];

const GEO_OPTIONS = [
  { value: "IN", label: "India (IN)" },
  { value: "US", label: "United States (US)" },
  { value: "GB", label: "United Kingdom (GB)" },
  { value: "AE", label: "UAE (AE)" },
  { value: "SG", label: "Singapore (SG)" },
  { value: "AU", label: "Australia (AU)" },
  { value: "CA", label: "Canada (CA)" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "DRAFT", label: "Draft" },
];

const REDIRECTION_TYPE_OPTIONS: { value: AdButtonRedirectionType; label: string }[] = [
  { value: "URL", label: "URL" },
  { value: "Page", label: "Page" },
  { value: "Custom", label: "Custom" },
  { value: "Academy", label: "Academy" },
  { value: "AcademyDetailsScreen", label: "Academy Details" },
  { value: "TournamentScreen", label: "Tournament Screen" },
  { value: "TournamentTab", label: "Tournament Tab" },
  { value: "WeeklyReport", label: "Weekly Report" },
];

const TAB_NAME_OPTIONS = [
  { value: "Home", label: "Home" },
  { value: "Fixtures", label: "Fixtures" },
  { value: "Standings", label: "Standings" },
  { value: "Player Stats", label: "Player Stats" },
  { value: "Highlights", label: "Highlights" },
  { value: "Voting", label: "Voting" },
  { value: "Awards", label: "Awards" },
  { value: "My Stats", label: "My Stats" },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PORTAL_STYLES = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

const ICON_POSITION_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
];

const DEFAULT_BUTTON: AdButton = {
  category: "cta",
  label: "",
  color: "#006eff",
  textColor: "#FFFFFF",
  titleColor: "#FFFFFF",
  icon: "",
  iconPosition: "right",
  image: "",
  redirectionType: "URL",
  arrow: { left: false, right: true },
  desc: "",
};

const EMPTY_FORM: FormState = {
  campaign_id: "",
  campaign_name: "",
  placement_id: "",
  placement_type: "",
  placement_label: "",
  creative_type: "",
  media_url: "",
  thumbnail_url: "",
  click_url: "",
  is_internal: false,
  aspect_ratio: "",
  duration: "",
  platform_name: "",
  tournament_ids: [],
  geo: [],
  platform: "",
  banner_position: "",
  buttons: [],
  status: "ACTIVE",
};

function formatPlacementName(type: string): string {
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// ─── ColorPickerInput ────────────────────────────────────────────────────────

const ColorPickerInput: React.FC<{ color: string; onChange: (c: string) => void }> = ({
  color,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="cad-color-picker">
      <div
        className="cad-color-picker__swatch"
        style={{ background: color }}
        onClick={() => setOpen((v) => !v)}
        title={color}
      />
      {open && (
        <div className="cad-color-picker__popover">
          <div className="cad-color-picker__cover" onClick={() => setOpen(false)} />
          <SketchPicker color={color} onChange={(c: ColorResult) => onChange(c.hex)} />
        </div>
      )}
    </div>
  );
};

// ─── AdButtonItem ────────────────────────────────────────────────────────────

interface AdButtonItemProps {
  button: AdButton;
  index: number;
  errors: Record<string, string>;
  disabled: boolean;
  onChange: (index: number, button: AdButton) => void;
  onRemove: (index: number) => void;
}

const AdButtonItem: React.FC<AdButtonItemProps> = ({
  button,
  index,
  errors,
  disabled,
  onChange,
  onRemove,
}) => {
  const [academyOptions, setAcademyOptions] = useState<any[]>([]);
  const [tournamentOptions, setTournamentOptions] = useState<any[]>([]);
  const [weeklyReportOptions, setWeeklyReportOptions] = useState<any[]>([]);
  const [seasonOptions, setSeasonOptions] = useState<any[]>([]);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [defaultSeason, setDefaultSeason] = useState<TournamentSeason | null>(null);
  const [hasSeasonSeasons, setHasSeasonSeasons] = useState(false);

  const update = (partial: Partial<AdButton>) => onChange(index, { ...button, ...partial });
  const updateArrow = (partial: Partial<AdButton["arrow"]>) =>
    onChange(index, { ...button, arrow: { ...button.arrow, ...partial } });

  const loadAcademies = async (inputValue: string) => {
    const data = await getAcademyDropdown();
    const options = data.map((acc: any) => ({
      value: acc._id,
      label: `${acc.academyInfo.basicDetails.academyName} (${acc.basicDetails.location})`,
      academyId: acc.academyInfo._id,
      academyName: acc.academyInfo.basicDetails.academyName,
      coachingCenterId: acc._id,
      academyLocation: acc.basicDetails.location,
    }));
    return options.filter((i: any) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  const loadTournaments = async (inputValue: string) => {
    const data = await getTournamentDropdown();
    const options = data.map((t: any) => ({ value: t._id, label: t.name, sports: t.sports }));
    return options.filter((i: any) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  const loadWeeklyReports = async (inputValue: string) => {
    const data = await getWeeklyReportDropdown();
    const options = data.map((r: any) => ({ value: r._id, label: r.title }));
    return options.filter((i: any) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  useEffect(() => {
    const loadSeasons = async () => {
      if (
        (button.redirectionType === "TournamentScreen" || button.redirectionType === "TournamentTab") &&
        button.tournamentId
      ) {
        setSeasonsLoading(true);
        try {
          const allSeasons = await getAllTournamentSeasons(button.tournamentId, button.sports || "Football");
          const defaultSeasonFound = allSeasons.find((s: TournamentSeason) => s.seasonType === "DEFAULT");
          setDefaultSeason(defaultSeasonFound || null);
          const seasonTypeSeasons = allSeasons.filter((s: TournamentSeason) => s.seasonType === "SEASON");
          setHasSeasonSeasons(seasonTypeSeasons.length > 0);
          setSeasonOptions(
            seasonTypeSeasons.map((s: TournamentSeason) => ({
              value: s._id,
              label: s.seasonLabel || s.seasonYearRange || `Season ${s.seasonNumber}`,
              seasonType: s.seasonType,
            }))
          );
          if (seasonTypeSeasons.length === 0 && defaultSeasonFound && !button.tournamentSeason) {
            update({ tournamentSeason: defaultSeasonFound._id, seasonName: undefined });
          }
        } catch {
          setSeasonOptions([]);
          setDefaultSeason(null);
          setHasSeasonSeasons(false);
        } finally {
          setSeasonsLoading(false);
        }
      } else {
        setSeasonOptions([]);
        setDefaultSeason(null);
        setHasSeasonSeasons(false);
      }
    };
    loadSeasons();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [button.redirectionType, button.tournamentId]);

  return (
    <div className="cad-button-item">
      <div className="cad-button-item__header">
        <span className="cad-button-item__title">Button {index + 1}</span>
        <button
          type="button"
          className="cad-button-item__remove"
          onClick={() => onRemove(index)}
          disabled={disabled}
          aria-label={`Remove button ${index + 1}`}
        >
          <i className="ph-trash" />
        </button>
      </div>

      <div className="cad-two-col">
        <div className="cad-field">
          <label className="cad-label">
            Label <span className="cad-required">*</span>
          </label>
          <input
            className={`cad-input${errors[`button_${index}_label`] ? " cad-input--error" : ""}`}
            value={button.label}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="e.g. Shop Now"
            disabled={disabled}
          />
          {errors[`button_${index}_label`] && (
            <p className="cad-field-error">{errors[`button_${index}_label`]}</p>
          )}
        </div>
        <div className="cad-field">
          <label className="cad-label">
            Category <span className="cad-required">*</span>
          </label>
          <input
            className={`cad-input${errors[`button_${index}_category`] ? " cad-input--error" : ""}`}
            value={button.category}
            onChange={(e) => update({ category: e.target.value })}
            placeholder="cta"
            disabled={disabled}
          />
          {errors[`button_${index}_category`] && (
            <p className="cad-field-error">{errors[`button_${index}_category`]}</p>
          )}
        </div>
      </div>

      <div className="cad-two-col">
        <div className="cad-field">
          <label className="cad-label">
            Icon <span className="cad-required">*</span>
          </label>
          <input
            className={`cad-input${errors[`button_${index}_icon`] ? " cad-input--error" : ""}`}
            value={button.icon}
            onChange={(e) => update({ icon: e.target.value })}
            placeholder="arrow-right"
            disabled={disabled}
          />
          {errors[`button_${index}_icon`] && (
            <p className="cad-field-error">{errors[`button_${index}_icon`]}</p>
          )}
        </div>
        <div className="cad-field">
          <label className="cad-label">
            Icon Position <span className="cad-required">*</span>
          </label>
          <Select
            options={ICON_POSITION_OPTIONS}
            value={ICON_POSITION_OPTIONS.find((o) => o.value === button.iconPosition) ?? null}
            onChange={(opt) => update({ iconPosition: opt?.value ?? "right" })}
            classNamePrefix="cad-select"
            menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
            menuPosition="fixed"
            isDisabled={disabled}
          />
        </div>
      </div>

      <div className="cad-three-col">
        <div className="cad-field">
          <label className="cad-label">
            Button Color <span className="cad-required">*</span>
          </label>
          <ColorPickerInput color={button.color} onChange={(color) => update({ color })} />
        </div>
        <div className="cad-field">
          <label className="cad-label">
            Text Color <span className="cad-required">*</span>
          </label>
          <ColorPickerInput color={button.textColor} onChange={(textColor) => update({ textColor })} />
        </div>
        <div className="cad-field">
          <label className="cad-label">
            Title Color <span className="cad-required">*</span>
          </label>
          <ColorPickerInput color={button.titleColor} onChange={(titleColor) => update({ titleColor })} />
        </div>
      </div>

      <div className="cad-two-col">
        <div className="cad-field">
          <label className="cad-label">
            Description <span className="cad-required">*</span>
          </label>
          <input
            className={`cad-input${errors[`button_${index}_desc`] ? " cad-input--error" : ""}`}
            value={button.desc}
            onChange={(e) => update({ desc: e.target.value })}
            placeholder="e.g. Limited time offer"
            disabled={disabled}
          />
          {errors[`button_${index}_desc`] && (
            <p className="cad-field-error">{errors[`button_${index}_desc`]}</p>
          )}
        </div>
        <div className="cad-field">
          <label className="cad-label">
            Image URL <span className="cad-required">*</span>
          </label>
          <input
            className={`cad-input${errors[`button_${index}_image`] ? " cad-input--error" : ""}`}
            value={button.image}
            onChange={(e) => update({ image: e.target.value })}
            placeholder="https://..."
            disabled={disabled}
          />
          {errors[`button_${index}_image`] && (
            <p className="cad-field-error">{errors[`button_${index}_image`]}</p>
          )}
        </div>
      </div>

      {/* ── Redirection ── */}
      <div className="cad-field">
        <label className="cad-label">
          Redirection Type <span className="cad-required">*</span>
        </label>
        <Select
          options={REDIRECTION_TYPE_OPTIONS}
          value={REDIRECTION_TYPE_OPTIONS.find((o) => o.value === button.redirectionType) ?? null}
          onChange={(opt) => update({ redirectionType: opt?.value ?? "URL" })}
          classNamePrefix="cad-select"
          menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
          menuPosition="fixed"
          isDisabled={disabled}
        />
      </div>

      {button.redirectionType === "URL" && (
        <div className="cad-field">
          <label className="cad-label">
            URL <span className="cad-required">*</span>
          </label>
          <input
            className={`cad-input${errors[`button_${index}_url`] ? " cad-input--error" : ""}`}
            value={button.url || ""}
            onChange={(e) => update({ url: e.target.value })}
            placeholder="https://example.com"
            disabled={disabled}
          />
          {errors[`button_${index}_url`] && (
            <p className="cad-field-error">{errors[`button_${index}_url`]}</p>
          )}
        </div>
      )}

      {button.redirectionType === "Page" && (
        <div className="cad-field">
          <label className="cad-label">
            Page <span className="cad-required">*</span>
          </label>
          <input
            className={`cad-input${errors[`button_${index}_page`] ? " cad-input--error" : ""}`}
            value={button.page || ""}
            onChange={(e) => update({ page: e.target.value })}
            placeholder="Enter page path"
            disabled={disabled}
          />
          {errors[`button_${index}_page`] && (
            <p className="cad-field-error">{errors[`button_${index}_page`]}</p>
          )}
        </div>
      )}

      {button.redirectionType === "Custom" && (
        <>
          <div className="cad-field">
            <label className="cad-label">
              Custom Type <span className="cad-required">*</span>
            </label>
            <input
              className={`cad-input${errors[`button_${index}_customType`] ? " cad-input--error" : ""}`}
              value={button.customType || ""}
              onChange={(e) => update({ customType: e.target.value })}
              placeholder="Enter custom type"
              disabled={disabled}
            />
            {errors[`button_${index}_customType`] && (
              <p className="cad-field-error">{errors[`button_${index}_customType`]}</p>
            )}
          </div>
          <div className="cad-field">
            <label className="cad-label">
              Custom Path <span className="cad-required">*</span>
            </label>
            <input
              className={`cad-input${errors[`button_${index}_customPath`] ? " cad-input--error" : ""}`}
              value={button.customPath || ""}
              onChange={(e) => update({ customPath: e.target.value })}
              placeholder="Enter custom path"
              disabled={disabled}
            />
            {errors[`button_${index}_customPath`] && (
              <p className="cad-field-error">{errors[`button_${index}_customPath`]}</p>
            )}
          </div>
        </>
      )}

      {button.redirectionType === "AcademyDetailsScreen" && (
        <div className="cad-field">
          <label className="cad-label">
            Academy <span className="cad-required">*</span>
          </label>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={async (inputValue) => {
              const options = await loadAcademies(inputValue);
              setAcademyOptions(options);
              return options;
            }}
            value={
              button.academyId
                ? academyOptions.find((o) => o.value === button.academyId) || {
                    value: button.academyId,
                    label: `${button.academyName}${button.academyLocation ? ` (${button.academyLocation})` : ""}`,
                  }
                : null
            }
            onChange={(option: any) =>
              update({
                academyId: option?.academyId || "",
                academyName: option?.academyName || "",
                coachingCenterId: option?.coachingCenterId || "",
                academyLocation: option?.academyLocation || "",
              })
            }
            classNamePrefix="cad-select"
            menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
            menuPosition="fixed"
            isDisabled={disabled}
          />
          {errors[`button_${index}_academyId`] && (
            <p className="cad-field-error">{errors[`button_${index}_academyId`]}</p>
          )}
        </div>
      )}

      {(button.redirectionType === "TournamentScreen" || button.redirectionType === "TournamentTab") && (
        <>
          <div className="cad-field">
            <label className="cad-label">
              Tournament <span className="cad-required">*</span>
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={async (inputValue) => {
                const options = await loadTournaments(inputValue);
                setTournamentOptions(options);
                return options;
              }}
              value={
                button.tournamentId
                  ? tournamentOptions.find((o) => o.value === button.tournamentId) ||
                    (button.tournamentName
                      ? { value: button.tournamentId, label: button.tournamentName, sports: button.sports }
                      : null)
                  : null
              }
              onChange={(option: any) =>
                update({
                  tournamentId: option?.value,
                  tournamentName: option?.label,
                  sports: option?.sports,
                  tournamentSeason: undefined,
                  seasonName: undefined,
                })
              }
              classNamePrefix="cad-select"
              menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
              menuPosition="fixed"
              isDisabled={disabled}
            />
            {errors[`button_${index}_tournamentId`] && (
              <p className="cad-field-error">{errors[`button_${index}_tournamentId`]}</p>
            )}
          </div>

          {button.tournamentId && (
            <div className="cad-field">
              <label className="cad-label">
                Season <span className="cad-required">*</span>
              </label>
              {!hasSeasonSeasons && defaultSeason ? (
                <input
                  className="cad-input"
                  value={defaultSeason.seasonLabel || defaultSeason.seasonYearRange || "Default Season"}
                  readOnly
                  disabled
                />
              ) : (
                <Select
                  isLoading={seasonsLoading}
                  options={seasonOptions}
                  value={
                    button.tournamentSeason
                      ? seasonOptions.find((o) => o.value === button.tournamentSeason) ||
                        (button.seasonName
                          ? { value: button.tournamentSeason, label: button.seasonName }
                          : null)
                      : null
                  }
                  onChange={(option: any) => {
                    const sel = seasonOptions.find((o) => o.value === option?.value);
                    update({
                      tournamentSeason: option?.value,
                      seasonName: sel?.seasonType === "SEASON" ? option?.label : undefined,
                    });
                  }}
                  classNamePrefix="cad-select"
                  menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  placeholder={seasonsLoading ? "Loading seasons..." : "Select season"}
                  isClearable={hasSeasonSeasons}
                  isDisabled={!hasSeasonSeasons && !!defaultSeason}
                />
              )}
              {!hasSeasonSeasons && defaultSeason && (
                <p className="cad-field-help">Using default season (no additional seasons available)</p>
              )}
              {errors[`button_${index}_tournamentSeason`] && (
                <p className="cad-field-error">{errors[`button_${index}_tournamentSeason`]}</p>
              )}
            </div>
          )}
        </>
      )}

      {button.redirectionType === "TournamentTab" && (
        <div className="cad-field">
          <label className="cad-label">
            Tab Name <span className="cad-required">*</span>
          </label>
          <Select
            options={TAB_NAME_OPTIONS}
            value={TAB_NAME_OPTIONS.find((o) => o.value === button.tabName) || null}
            onChange={(option: any) => update({ tabName: option?.value })}
            classNamePrefix="cad-select"
            menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
            menuPosition="fixed"
            isDisabled={disabled}
          />
          {errors[`button_${index}_tabName`] && (
            <p className="cad-field-error">{errors[`button_${index}_tabName`]}</p>
          )}
        </div>
      )}

      {button.redirectionType === "WeeklyReport" && (
        <div className="cad-field">
          <label className="cad-label">
            Weekly Report <span className="cad-required">*</span>
          </label>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={async (inputValue) => {
              const options = await loadWeeklyReports(inputValue);
              setWeeklyReportOptions(options);
              return options;
            }}
            value={
              button.weeklyReportId
                ? weeklyReportOptions.find((o) => o.value === button.weeklyReportId) ||
                  (button.weeklyReportName
                    ? { value: button.weeklyReportId, label: button.weeklyReportName }
                    : null)
                : null
            }
            onChange={(option: any) =>
              update({ weeklyReportId: option?.value, weeklyReportName: option?.label })
            }
            classNamePrefix="cad-select"
            menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
            menuPosition="fixed"
            isDisabled={disabled}
          />
          {errors[`button_${index}_weeklyReportId`] && (
            <p className="cad-field-error">{errors[`button_${index}_weeklyReportId`]}</p>
          )}
        </div>
      )}

      <div className="cad-field">
        <label className="cad-label">Arrow</label>
        <div className="cad-checkbox-row">
          <label className="cad-checkbox-label">
            <input
              type="checkbox"
              checked={button.arrow.left}
              onChange={(e) => updateArrow({ left: e.target.checked })}
              disabled={disabled}
            />
            Left
          </label>
          <label className="cad-checkbox-label">
            <input
              type="checkbox"
              checked={button.arrow.right}
              onChange={(e) => updateArrow({ right: e.target.checked })}
              disabled={disabled}
            />
            Right
          </label>
        </div>
      </div>
    </div>
  );
};

// ─── CreateAdModal ────────────────────────────────────────────────────────────

export interface CreateAdModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateAdPayload) => Promise<void>;
  onUpdate?: (id: string, payload: UpdateAdPayload) => Promise<void>;
  submitting: boolean;
  error?: string | null;
  editAdId?: string;
}

export const CreateAdModal: React.FC<CreateAdModalProps> = ({
  show,
  onClose,
  onSubmit,
  onUpdate,
  submitting,
  error,
  editAdId,
}) => {
  const isEditMode = Boolean(editAdId);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [placements, setPlacements] = useState<PlacementOption[]>([]);
  const [placementsLoading, setPlacementsLoading] = useState(false);
  const [loadingAd, setLoadingAd] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<SelectOption | null>(null);
  const [selectedTournaments, setSelectedTournaments] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!show) return;

    if (!editAdId) {
      setForm(EMPTY_FORM);
      setValidationErrors({});
      setSelectedCampaign(null);
      setSelectedTournaments([]);
      return;
    }

    setLoadingAd(true);
    setValidationErrors({});
    getAdById(editAdId)
      .then((data: any) => {
        const campaignId =
          typeof data.campaign_id === "object"
            ? (data.campaign_id._id ?? "")
            : (data.campaign_id ?? "");
        const campaignName =
          typeof data.campaign_id === "object" ? (data.campaign_id.name ?? "") : "";
        const placementId =
          typeof data.placement_id === "object"
            ? (data.placement_id._id ?? "")
            : (data.placement_id ?? "");
        const placementType =
          data.placement_type ??
          (typeof data.placement_id === "object" ? data.placement_id.type : "") ??
          "";

        const buttons: AdButton[] = (data.buttons ?? []).map((btn: any): AdButton => ({
          category: btn.category ?? "cta",
          label: btn.label ?? "",
          color: btn.color ?? "#006eff",
          textColor: btn.textColor ?? "#FFFFFF",
          titleColor: btn.titleColor ?? "#FFFFFF",
          icon: btn.icon ?? "",
          iconPosition: btn.iconPosition ?? "right",
          image: btn.image ?? "",
          redirectionType: "URL" as AdButtonRedirectionType,
          url: btn.redirection?.path ?? "",
          arrow: btn.arrow ?? { left: false, right: true },
          desc: btn.desc ?? "",
        }));

        const tournamentIds: string[] = data.targeting?.tournament_ids ?? [];

        setForm({
          campaign_id: campaignId,
          campaign_name: campaignName,
          placement_id: placementId,
          placement_type: placementType,
          placement_label: formatPlacementName(placementType),
          creative_type: (data.creative?.type ?? "") as FormState["creative_type"],
          media_url: data.creative?.media_url ?? "",
          thumbnail_url: data.creative?.thumbnail_url ?? "",
          click_url: data.creative?.click_url ?? "",
          is_internal: data.creative?.is_internal ?? false,
          aspect_ratio: data.creative?.aspect_ratio ?? "",
          duration:
            typeof data.creative?.duration === "number" ? data.creative.duration : "",
          platform_name: data.creative?.platform_name ?? "",
          tournament_ids: tournamentIds,
          geo: data.targeting?.geo ?? [],
          platform: data.targeting?.platform ?? "",
          banner_position:
            typeof data.banner_position === "number" ? data.banner_position : "",
          buttons,
          status: data.status ?? "ACTIVE",
        });

        setSelectedCampaign(
          campaignId ? { value: campaignId, label: campaignName } : null
        );
        setSelectedTournaments(tournamentIds.map((id) => ({ value: id, label: id })));
      })
      .catch(() => {})
      .finally(() => setLoadingAd(false));
  }, [show, editAdId]);

  useEffect(() => {
    if (!show) return;
    setPlacementsLoading(true);
    getPlacements()
      .then(({ results }) => {
        setPlacements(
          results.map((p) => ({
            value: p.id,
            label: formatPlacementName(p.name),
            type: p.name,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setPlacementsLoading(false));
  }, [show]);

  const loadCampaigns = useCallback(async (inputValue: string) => {
    const { results } = await getAdCampaigns({ search: inputValue, pageSize: 20 });
    return results.map((c) => ({ value: c.id, label: c.name }));
  }, []);

  const loadTournaments = useCallback(async (inputValue: string) => {
    const results = await getTournamentsForAds(inputValue || undefined);
    return results.map((t) => ({ value: t.id, label: t.name }));
  }, []);

  const isBannerType = form.placement_type.toLowerCase().includes("banner");

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.campaign_id) errors.campaign_id = "Campaign is required.";
    if (!form.placement_id) errors.placement_id = "Placement is required.";
    if (!form.media_url.trim()) errors.media_url = "Media URL is required.";
    if (!form.click_url.trim()) errors.click_url = "Click URL is required.";
    if (isBannerType) {
      form.buttons.forEach((btn, i) => {
        if (!btn.label.trim()) errors[`button_${i}_label`] = `Button ${i + 1}: Label is required.`;
        if (!btn.category.trim()) errors[`button_${i}_category`] = `Button ${i + 1}: Category is required.`;
        if (!btn.icon.trim()) errors[`button_${i}_icon`] = `Button ${i + 1}: Icon is required.`;
        if (!btn.desc.trim()) errors[`button_${i}_desc`] = `Button ${i + 1}: Description is required.`;
        if (!btn.image.trim()) errors[`button_${i}_image`] = `Button ${i + 1}: Image URL is required.`;

        if (btn.redirectionType === "URL") {
          if (!btn.url?.trim()) errors[`button_${i}_url`] = `Button ${i + 1}: URL is required.`;
        } else if (btn.redirectionType === "Page") {
          if (!btn.page?.trim()) errors[`button_${i}_page`] = `Button ${i + 1}: Page is required.`;
        } else if (btn.redirectionType === "Custom") {
          if (!btn.customType?.trim()) errors[`button_${i}_customType`] = `Button ${i + 1}: Custom type is required.`;
          if (!btn.customPath?.trim()) errors[`button_${i}_customPath`] = `Button ${i + 1}: Custom path is required.`;
        } else if (btn.redirectionType === "AcademyDetailsScreen") {
          if (!btn.academyId?.trim()) errors[`button_${i}_academyId`] = `Button ${i + 1}: Academy is required.`;
        } else if (btn.redirectionType === "TournamentScreen") {
          if (!btn.tournamentId?.trim()) errors[`button_${i}_tournamentId`] = `Button ${i + 1}: Tournament is required.`;
          if (!btn.tournamentSeason?.trim()) errors[`button_${i}_tournamentSeason`] = `Button ${i + 1}: Season is required.`;
        } else if (btn.redirectionType === "TournamentTab") {
          if (!btn.tournamentId?.trim()) errors[`button_${i}_tournamentId`] = `Button ${i + 1}: Tournament is required.`;
          if (!btn.tournamentSeason?.trim()) errors[`button_${i}_tournamentSeason`] = `Button ${i + 1}: Season is required.`;
          if (!btn.tabName?.trim()) errors[`button_${i}_tabName`] = `Button ${i + 1}: Tab name is required.`;
        } else if (btn.redirectionType === "WeeklyReport") {
          if (!btn.weeklyReportId?.trim()) errors[`button_${i}_weeklyReportId`] = `Button ${i + 1}: Weekly report is required.`;
        }
      });
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const creative = {
      media_url: form.media_url.trim(),
      click_url: form.click_url.trim(),
      ...(form.creative_type && { type: form.creative_type }),
      ...(form.thumbnail_url.trim() && { thumbnail_url: form.thumbnail_url.trim() }),
      is_internal: form.is_internal,
      ...(form.aspect_ratio.trim() && { aspect_ratio: form.aspect_ratio.trim() }),
      ...(form.creative_type === "VIDEO" &&
        form.duration !== "" && { duration: Number(form.duration) }),
      ...(form.platform_name.trim() && { platform_name: form.platform_name.trim() }),
    };

    const hasTargeting =
      form.tournament_ids.length > 0 || form.geo.length > 0 || Boolean(form.platform);
    const targeting = hasTargeting
      ? {
          ...(form.tournament_ids.length > 0 && { tournament_ids: form.tournament_ids }),
          ...(form.geo.length > 0 && { geo: form.geo }),
          ...(form.platform && { platform: form.platform }),
        }
      : undefined;

    if (isEditMode && editAdId && onUpdate) {
      const updatePayload: UpdateAdPayload = {
        creative,
        ...(targeting && { targeting }),
        ...(isBannerType && form.banner_position !== "" && { banner_position: Number(form.banner_position) }),
        ...(isBannerType && form.buttons.length > 0 && { buttons: form.buttons }),
      };
      await onUpdate(editAdId, updatePayload);
      return;
    }

    const payload: CreateAdPayload = {
      campaign_id: form.campaign_id,
      placement_id: form.placement_id,
      placement_type: form.placement_type,
      creative,
      status: form.status,
      ...(targeting && { targeting }),
      ...(isBannerType && form.banner_position !== "" && { banner_position: Number(form.banner_position) }),
      ...(isBannerType && form.buttons.length > 0 && { buttons: form.buttons }),
    };

    await onSubmit(payload);
  };

  const handleButtonChange = (index: number, button: AdButton) => {
    setForm((prev) => {
      const buttons = [...prev.buttons];
      buttons[index] = button;
      return { ...prev, buttons };
    });
  };

  const handleButtonRemove = (index: number) => {
    setForm((prev) => ({ ...prev, buttons: prev.buttons.filter((_, i) => i !== index) }));
  };

  const selectedPlacement = form.placement_id
    ? placements.find((p) => p.value === form.placement_id) ?? null
    : null;

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" className="cad-drawer">
      <form onSubmit={handleSubmit} noValidate className="cad-drawer__form">
        <div className="cad-modal__header">
          <span className="cad-modal__title">{isEditMode ? "Edit Ad" : "Create Ad"}</span>
          <button
            type="button"
            className="cad-modal__close"
            onClick={onClose}
            disabled={submitting || loadingAd}
            aria-label="Close"
          >
            <i className="ph-x" />
          </button>
        </div>

        <div className="cad-modal__body">
          {loadingAd && (
            <div className="cad-loading">
              <span>Loading ad details...</span>
            </div>
          )}

          {/* ── Campaign & Placement ── */}
          <div className="cad-section">
            <h6 className="cad-section__title">Campaign & Placement</h6>

            <div className="cad-field">
              <label className="cad-label">
                Campaign <span className="cad-required">*</span>
              </label>
              {isEditMode ? (
                <input
                  className="cad-input"
                  value={form.campaign_name || form.campaign_id}
                  disabled
                  readOnly
                />
              ) : (
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCampaigns}
                  placeholder="Search campaigns..."
                  value={selectedCampaign}
                  onChange={(opt) => {
                    setSelectedCampaign(opt as SelectOption | null);
                    setForm((prev) => ({
                      ...prev,
                      campaign_id: opt?.value ?? "",
                      campaign_name: opt?.label ?? "",
                    }));
                  }}
                  classNamePrefix="cad-select"
                  menuPortalTarget={document.body}
                  styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  isDisabled={submitting}
                />
              )}
              {validationErrors.campaign_id && (
                <p className="cad-field-error">{validationErrors.campaign_id}</p>
              )}
            </div>

            <div className="cad-field">
              <label className="cad-label">
                Placement <span className="cad-required">*</span>
              </label>
              {isEditMode ? (
                <input
                  className="cad-input"
                  value={form.placement_label || form.placement_type}
                  disabled
                  readOnly
                />
              ) : (
                <Select
                  options={placements}
                  isLoading={placementsLoading}
                  placeholder="Select placement..."
                  value={selectedPlacement}
                  onChange={(opt) => {
                    const p = opt as PlacementOption | null;
                    setForm((prev) => ({
                      ...prev,
                      placement_id: p?.value ?? "",
                      placement_type: p?.type ?? "",
                      placement_label: p?.label ?? "",
                      buttons: p?.type?.toLowerCase().includes("banner") ? prev.buttons : [],
                      banner_position: p?.type?.toLowerCase().includes("banner")
                        ? prev.banner_position
                        : "",
                    }));
                  }}
                  classNamePrefix="cad-select"
                  menuPortalTarget={document.body}
                  styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  isDisabled={submitting}
                />
              )}
              {validationErrors.placement_id && (
                <p className="cad-field-error">{validationErrors.placement_id}</p>
              )}
            </div>
          </div>

          {/* ── Creative ── */}
          <div className="cad-section">
            <h6 className="cad-section__title">Creative</h6>

            <div className="cad-two-col">
              <div className="cad-field">
                <label className="cad-label">Type</label>
                <Select
                  options={CREATIVE_TYPE_OPTIONS}
                  value={
                    CREATIVE_TYPE_OPTIONS.find((o) => o.value === form.creative_type) ??
                    CREATIVE_TYPE_OPTIONS[0]
                  }
                  onChange={(opt) =>
                    setForm((prev) => ({
                      ...prev,
                      creative_type: (opt?.value as FormState["creative_type"]) ?? "",
                      ...(opt?.value !== "VIDEO" && { duration: "" }),
                    }))
                  }
                  classNamePrefix="cad-select"
                  menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  isDisabled={submitting}
                />
              </div>
              <div className="cad-field">
                <label className="cad-label">Aspect Ratio</label>
                <input
                  className="cad-input"
                  value={form.aspect_ratio}
                  onChange={(e) => setForm((prev) => ({ ...prev, aspect_ratio: e.target.value }))}
                  placeholder="e.g. 16:9"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="cad-field">
              <label className="cad-label">
                Media URL <span className="cad-required">*</span>
              </label>
              <input
                className={`cad-input${validationErrors.media_url ? " cad-input--error" : ""}`}
                value={form.media_url}
                onChange={(e) => setForm((prev) => ({ ...prev, media_url: e.target.value }))}
                placeholder="https://cdn.sfl.in/ads/..."
                disabled={submitting}
              />
              {validationErrors.media_url && (
                <p className="cad-field-error">{validationErrors.media_url}</p>
              )}
            </div>

            <div className="cad-field">
              <label className="cad-label">Thumbnail URL</label>
              <input
                className="cad-input"
                value={form.thumbnail_url}
                onChange={(e) => setForm((prev) => ({ ...prev, thumbnail_url: e.target.value }))}
                placeholder="https://cdn.sfl.in/ads/thumb-..."
                disabled={submitting}
              />
            </div>

            <div className="cad-field">
              <label className="cad-label">
                Click URL <span className="cad-required">*</span>
              </label>
              <input
                className={`cad-input${validationErrors.click_url ? " cad-input--error" : ""}`}
                value={form.click_url}
                onChange={(e) => setForm((prev) => ({ ...prev, click_url: e.target.value }))}
                placeholder="https://..."
                disabled={submitting}
              />
              {validationErrors.click_url && (
                <p className="cad-field-error">{validationErrors.click_url}</p>
              )}
            </div>

            <div className="cad-field">
              <label className="cad-label">Platform Name</label>
              <input
                className="cad-input"
                value={form.platform_name}
                onChange={(e) => setForm((prev) => ({ ...prev, platform_name: e.target.value }))}
                placeholder="e.g. Android, iOS, Web"
                disabled={submitting}
              />
            </div>

            {form.creative_type === "VIDEO" && (
              <div className="cad-field" style={{ maxWidth: 200 }}>
                <label className="cad-label">Duration (seconds)</label>
                <input
                  type="number"
                  className="cad-input"
                  value={form.duration}
                  min={0}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      duration: e.target.value === "" ? "" : Number(e.target.value),
                    }))
                  }
                  placeholder="e.g. 30"
                  disabled={submitting}
                />
              </div>
            )}

            <div className="cad-field">
              <label className="cad-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.is_internal}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_internal: e.target.checked }))}
                  disabled={submitting}
                />
                Is Internal
              </label>
            </div>
          </div>

          {/* ── Targeting ── */}
          <div className="cad-section">
            <h6 className="cad-section__title">Targeting</h6>

            <div className="cad-field">
              <label className="cad-label">Tournaments</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                isMulti
                loadOptions={loadTournaments}
                placeholder="Search tournaments..."
                value={selectedTournaments}
                onChange={(opts) => {
                  const selected = (opts as SelectOption[]) ?? [];
                  setSelectedTournaments(selected);
                  setForm((prev) => ({
                    ...prev,
                    tournament_ids: selected.map((o) => o.value),
                  }));
                }}
                classNamePrefix="cad-select"
                menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
                menuPosition="fixed"
                isDisabled={submitting}
              />
            </div>

            <div className="cad-two-col">
              <div className="cad-field">
                <label className="cad-label">Geo Targeting</label>
                <Select
                  isMulti
                  options={GEO_OPTIONS}
                  value={GEO_OPTIONS.filter((o) => form.geo.includes(o.value))}
                  onChange={(opts) =>
                    setForm((prev) => ({
                      ...prev,
                      geo: opts ? (opts as typeof GEO_OPTIONS).map((o) => o.value) : [],
                    }))
                  }
                  classNamePrefix="cad-select"
                  menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  placeholder="Select countries..."
                  isDisabled={submitting}
                />
              </div>
              <div className="cad-field">
                <label className="cad-label">Platform</label>
                <Select
                  options={PLATFORM_OPTIONS}
                  value={
                    PLATFORM_OPTIONS.find((o) => o.value === form.platform) ?? PLATFORM_OPTIONS[0]
                  }
                  onChange={(opt) =>
                    setForm((prev) => ({ ...prev, platform: opt?.value ?? "" }))
                  }
                  classNamePrefix="cad-select"
                  menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  isDisabled={submitting}
                />
              </div>
            </div>
          </div>

          {/* ── Banner Configuration (shown only for banner placement types) ── */}
          {isBannerType && (
            <div className="cad-section">
              <h6 className="cad-section__title">Banner Configuration</h6>

              <div className="cad-field" style={{ maxWidth: 160 }}>
                <label className="cad-label">Banner Position</label>
                <input
                  type="number"
                  className="cad-input"
                  value={form.banner_position}
                  min={1}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      banner_position: e.target.value === "" ? "" : Number(e.target.value),
                    }))
                  }
                  placeholder="1"
                  disabled={submitting}
                />
              </div>

              <div className="cad-section__subheader">
                <span className="cad-section__subtitle">Buttons</span>
                <button
                  type="button"
                  className="cad-add-btn"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      buttons: [...prev.buttons, { ...DEFAULT_BUTTON }],
                    }))
                  }
                  disabled={submitting || form.buttons.length >= 3}
                >
                  <i className="ph-plus" /> Add Button
                </button>
              </div>

              {form.buttons.length === 0 ? (
                <p className="cad-empty-text">No buttons added.</p>
              ) : (
                form.buttons.map((btn, i) => (
                  <AdButtonItem
                    key={i}
                    button={btn}
                    index={i}
                    errors={validationErrors}
                    disabled={submitting}
                    onChange={handleButtonChange}
                    onRemove={handleButtonRemove}
                  />
                ))
              )}
            </div>
          )}

          {/* ── Status ── */}
          <div className="cad-section">
            <h6 className="cad-section__title">Status</h6>
            <div className="cad-field" style={{ maxWidth: 200 }}>
              <Select
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === form.status) ?? STATUS_OPTIONS[0]}
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, status: opt?.value ?? "ACTIVE" }))
                }
                classNamePrefix="cad-select"
                menuPortalTarget={document.body}
            styles={PORTAL_STYLES}
                menuPosition="fixed"
                menuPlacement="auto"
                isDisabled={submitting}
              />
            </div>
          </div>
        </div>

        {error && <p className="cad-modal__api-error">{error}</p>}

        <div className="cad-modal__actions">
          <button
            type="button"
            className="cad-modal__btn cad-modal__btn--cancel"
            onClick={onClose}
            disabled={submitting || loadingAd}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="cad-modal__btn cad-modal__btn--confirm"
            disabled={submitting || loadingAd}
          >
            {submitting
              ? isEditMode ? "Updating..." : "Creating..."
              : isEditMode ? "Update Ad" : "Create Ad"}
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};
